import {
    IonBadge,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    americanFootballOutline,
    calendarOutline,
    chevronForward,
    closeOutline,
    downloadOutline,
    heartOutline,
    searchOutline,
    shareOutline,
    trophyOutline
} from "ionicons/icons";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Competition,
    Game,
    Team,
    getFavoriteTeamId,
    getHomeData
} from "../services/mexfbaApi";

import { getAuthenticatedUsername } from "../services/auth";

import "./Home.css";

interface GameGroup {
    id: string;
    competition?: Competition;
    date: string;
    games: Game[];
}

function parseGameDate(date?: string): Date | null {
    if (!date) {
        return null;
    }

    /*
        Las fechas con formato YYYY-MM-DD se interpretan
        al mediodía local para evitar cambios de día por zona horaria.
    */
    const normalizedDate =
        date.length === 10 ? `${date}T12:00:00` : date;

    const parsedDate = new Date(normalizedDate);

    if (Number.isNaN(parsedDate.getTime())) {
        return null;
    }

    return parsedDate;
}

function getStartOfToday(): Date {
    const today = new Date();

    today.setHours(0, 0, 0, 0);

    return today;
}

function formatDate(date: string): string {
    const parsedDate = parseGameDate(date);

    if (!parsedDate) {
        return "Fecha por confirmar";
    }

    return new Intl.DateTimeFormat("es-MX", {
        weekday: "long",
        day: "numeric",
        month: "long"
    }).format(parsedDate);
}

function getGameStatus(game: Game): string {
    const gameDate = parseGameDate(game.fecha);

    if (!gameDate) {
        return "Por confirmar";
    }

    const hasScore =
        game.resLocal !== "" &&
        game.resLocal !== null &&
        game.resLocal !== undefined &&
        game.resVisita !== "" &&
        game.resVisita !== null &&
        game.resVisita !== undefined;

    const today = getStartOfToday();

    if (hasScore && gameDate < today) {
        return "Final";
    }

    return "Próximo";
}
function hasValidScore(value: string | number | null | undefined): boolean {
    return (
        value !== "" &&
        value !== null &&
        value !== undefined &&
        Number.isFinite(Number(value))
    );
}

function isFinishedGame(game: Game): boolean {
    const gameDate = parseGameDate(game.fecha);

    if (!gameDate) {
        return false;
    }

    const hasScore =
        hasValidScore(game.resLocal) &&
        hasValidScore(game.resVisita);

    /*
        Se toma como finalizado cuando ya tiene marcador y su fecha
        es hoy o anterior. No dependemos de una hora específica.
    */
    return hasScore && gameDate <= getStartOfToday();
}

function formatCompactDate(date?: string): string {
    const parsedDate = parseGameDate(date);

    if (!parsedDate) {
        return "Fecha por confirmar";
    }

    return new Intl.DateTimeFormat("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric"
    }).format(parsedDate);
}

type InstallGuidePlatform =
    | "android"
    | "ios"
    | null;

const INSTALL_GUIDE_SEEN_KEY =
    "mexfba-install-guide-seen";

function isRunningAsInstalledApp(): boolean {
    const isStandaloneDisplayMode =
        window.matchMedia?.(
            "(display-mode: standalone)"
        ).matches;

    /*
        navigator.standalone es específico de Safari en iOS.
    */
    const isIOSStandalone =
        Boolean(
            (navigator as Navigator & {
                standalone?: boolean;
            }).standalone
        );

    return Boolean(
        isStandaloneDisplayMode ||
        isIOSStandalone
    );
}

function getInstallGuidePlatform(): InstallGuidePlatform {
    const userAgent =
        navigator.userAgent || "";

    /*
        iPadOS puede identificarse como MacIntel cuando usa
        teclado táctil, por eso se contempla maxTouchPoints.
    */
    const isIOS =
        /iPad|iPhone|iPod/i.test(userAgent) ||
        (
            navigator.platform === "MacIntel" &&
            navigator.maxTouchPoints > 1
        );

    if (isIOS) {
        return "ios";
    }

    if (/Android/i.test(userAgent)) {
        return "android";
    }

    return null;
}

const Home: React.FC = () => {
    const [games, setGames] = useState<Game[]>([]);
    const [teams, setTeams] = useState<Team[]>([]);
    const [competitions, setCompetitions] = useState<Competition[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const authenticatedUsername = getAuthenticatedUsername();
    const [installGuidePlatform, setInstallGuidePlatform] =
    useState<InstallGuidePlatform>(null);

    const [favoriteTeamId, setFavoriteTeamId] = useState<string | null>(
        null
    );

    const [favoriteLoading, setFavoriteLoading] = useState(
        Boolean(authenticatedUsername)
    );


    const loadHomeData = useCallback(
    async function (forceRefresh = false) {
        try {
            setError("");

            if (!forceRefresh) {
                setLoading(true);
            }

            if (authenticatedUsername) {
                setFavoriteLoading(true);
            }

            const [homeData, userFavoriteTeamId] = await Promise.all([
                getHomeData(forceRefresh),
                authenticatedUsername
                    ? getFavoriteTeamId(
                          authenticatedUsername,
                          forceRefresh
                      )
                    : Promise.resolve(null)
            ]);

            setGames(homeData.games);
            setTeams(homeData.teams);
            setCompetitions(homeData.competitions);
            setFavoriteTeamId(userFavoriteTeamId);
        } catch (requestError) {
            console.error("No fue posible cargar el inicio:", requestError);

            setError(
                "No fue posible cargar los partidos. Revisa tu conexión e inténtalo nuevamente."
            );
        } finally {
            setLoading(false);
            setFavoriteLoading(false);
        }
    },
    [authenticatedUsername]
);

    useEffect(function () {
    /*
        No se muestra dentro de la PWA instalada.
        Tampoco se repite si el usuario ya recibió la guía.
    */
    if (isRunningAsInstalledApp()) {
        return;
    }

    try {
        const guideWasAlreadyShown =
            window.localStorage.getItem(
                INSTALL_GUIDE_SEEN_KEY
            ) === "true";

        if (guideWasAlreadyShown) {
            return;
        }

        const platform =
            getInstallGuidePlatform();

        if (!platform) {
            return;
        }

        /*
            Se registra al mostrarse, no al cerrar el aviso.
            Así no vuelve a aparecer al regresar a Inicio.
        */
        window.localStorage.setItem(
            INSTALL_GUIDE_SEEN_KEY,
            "true"
        );

        setInstallGuidePlatform(platform);
    } catch (storageError) {
        /*
            Si el navegador bloquea localStorage, no impedimos
            que Inicio funcione. Simplemente mostramos la guía
            durante la sesión actual.
        */
        console.warn(
            "No fue posible guardar la guía de instalación:",
            storageError
        );

        setInstallGuidePlatform(
            getInstallGuidePlatform()
        );
    }
}, []);

    useEffect(function () {
        loadHomeData();
    }, [loadHomeData]);

    const teamMap = useMemo(function () {
        return new Map(
            teams.map(function (team) {
                return [String(team.id), team];
            })
        );
    }, [teams]);

    const competitionMap = useMemo(function () {
        return new Map(
            competitions.map(function (competition) {
                return [String(competition.id), competition];
            })
        );
    }, [competitions]);

    const favoriteTeam = useMemo(function () {
    if (!favoriteTeamId) {
        return null;
    }

    return teamMap.get(String(favoriteTeamId)) || null;
}, [favoriteTeamId, teamMap]);

const favoriteTeamGames = useMemo(function () {
    if (!favoriteTeamId) {
        return [];
    }

    return games.filter(function (game) {
        return (
            String(game.id_local) === String(favoriteTeamId) ||
            String(game.id_visita) === String(favoriteTeamId)
        );
    });
}, [games, favoriteTeamId]);

const lastFavoriteGame = useMemo(function () {
    return [...favoriteTeamGames]
        .filter(isFinishedGame)
        .sort(function (firstGame, secondGame) {
            const firstDate =
                parseGameDate(firstGame.fecha)?.getTime() || 0;

            const secondDate =
                parseGameDate(secondGame.fecha)?.getTime() || 0;

            return secondDate - firstDate;
        })[0];
}, [favoriteTeamGames]);

const nextFavoriteGame = useMemo(function () {
    const today = getStartOfToday();

    return [...favoriteTeamGames]
        .filter(function (game) {
            const gameDate = parseGameDate(game.fecha);

            return (
                !isFinishedGame(game) &&
                gameDate !== null &&
                gameDate >= today
            );
        })
        .sort(function (firstGame, secondGame) {
            const firstDate =
                parseGameDate(firstGame.fecha)?.getTime() || 0;

            const secondDate =
                parseGameDate(secondGame.fecha)?.getTime() || 0;

            return firstDate - secondDate;
        })[0];
}, [favoriteTeamGames]);

    const gameGroups = useMemo(function (): GameGroup[] {
        const today = getStartOfToday();
        const endDate = new Date(today);

        /*
            Inicio muestra juegos recientes y próximos.
            Más adelante "Partidos" tendrá el calendario completo.
        */
        today.setDate(today.getDate() - 2);
        endDate.setDate(endDate.getDate() + 7);

        const visibleGames = games
            .filter(function (game) {
                const gameDate = parseGameDate(game.fecha);

                if (!gameDate) {
                    return false;
                }

                return gameDate >= today && gameDate <= endDate;
            })
            .sort(function (firstGame, secondGame) {
                const firstDate = parseGameDate(firstGame.fecha)?.getTime() || 0;
                const secondDate =
                    parseGameDate(secondGame.fecha)?.getTime() || 0;

                return firstDate - secondDate;
            });

        const groups = new Map<string, GameGroup>();

        visibleGames.forEach(function (game) {
            const groupId = `${game.id_comp}-${game.fecha}`;
            const existingGroup = groups.get(groupId);

            if (existingGroup) {
                existingGroup.games.push(game);
                return;
            }

            groups.set(groupId, {
                id: groupId,
                competition: competitionMap.get(String(game.id_comp)),
                date: game.fecha || "",
                games: [game]
            });
        });

        return Array.from(groups.values());
    }, [games, competitionMap]);

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadHomeData(true);

        event.detail.complete();
    }

    function getTeamName(game: Game, side: "local" | "visita"): string {
        const id = side === "local" ? game.id_local : game.id_visita;
        const fallback =
            side === "local" ? game.nombre_local : game.nombre_visita;

        return teamMap.get(String(id))?.nombre || fallback || "Equipo";
    }

    function getTeamLogo(game: Game, side: "local"): string {
        return teamMap.get(String(game.id_local))?.logo || "";
    }

    function getVisitorLogo(game: Game): string {
        return teamMap.get(String(game.id_visita))?.logo || "";
    }

    function getFavoriteOpponent(game: Game): string {
    const isLocal = String(game.id_local) === String(favoriteTeamId);

    return isLocal
        ? getTeamName(game, "visita")
        : getTeamName(game, "local");
}

function getFavoriteTeamScore(game: Game): string | number {
    const isLocal = String(game.id_local) === String(favoriteTeamId);

    return isLocal ? game.resLocal ?? "—" : game.resVisita ?? "—";
}

function getOpponentScore(game: Game): string | number {
    const isLocal = String(game.id_local) === String(favoriteTeamId);

    return isLocal ? game.resVisita ?? "—" : game.resLocal ?? "—";
}

function getFavoriteVenue(game: Game): string {
    return String(game.id_local) === String(favoriteTeamId)
        ? "Local"
        : "Visita";
}

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="app-toolbar">
                    <IonTitle>MexFBA</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Buscar equipos, jugadores o competencias"
                            routerLink="/search"
                        >
                            <IonIcon icon={searchOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="home-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando partidos..."
                    />
                </IonRefresher>

                <IonHeader collapse="condense">
                    <IonToolbar>
                        <IonTitle size="large">MexFBA</IonTitle>
                    </IonToolbar>
                </IonHeader>
{installGuidePlatform && (
    <section
        className="install-guide"
        aria-label="Guía para instalar MexFBA"
    >
        <IonIcon
            className="install-guide__icon"
            icon={
                installGuidePlatform === "ios"
                    ? shareOutline
                    : downloadOutline
            }
        />

        <div className="install-guide__content">
            <span>INSTALA MEXFBA</span>

            {installGuidePlatform === "ios" ? (
                <p>
                    En <strong>Safari</strong>, toca
                    Compartir <IonIcon icon={shareOutline} /> y
                    selecciona <strong>“Agregar a pantalla de inicio”</strong>.
                </p>
            ) : (
                <p>
                    En Chrome, abre el menú <strong>⋮</strong> y
                    toca <strong>“Instalar app”</strong> o
                    <strong> “Agregar a pantalla principal”</strong>.
                </p>
            )}
        </div>

        <IonButton
            className="install-guide__close"
            fill="clear"
            color="medium"
            aria-label="Cerrar guía de instalación"
            onClick={function () {
                setInstallGuidePlatform(null);
            }}
        >
            <IonIcon icon={closeOutline} />
        </IonButton>
    </section>
)}
                <section className="home-hero">
                    <span className="home-hero__eyebrow">
                        TEMPORADA 2026
                    </span>

                    <h1>Tu football, al momento.</h1>

                    <p>
                        Consulta los partidos recientes y los próximos encuentros.
                    </p>

                    <IonButton
                        fill="clear"
                        className="home-hero__link"
                        routerLink="/tournment"
                    >
                        Explorar competencias
                        <IonIcon slot="end" icon={chevronForward} />
                    </IonButton>
                </section>
{authenticatedUsername && (
    <section className="favorite-section">
        <div className="home-section__heading">
            <div>
                <span className="home-section__eyebrow">
                    MI EQUIPO
                </span>

                <h2>Favorito</h2>
            </div>

            <IonIcon
                className="favorite-section__icon"
                icon={heartOutline}
            />
        </div>

        {favoriteLoading && (
            <IonProgressBar
                className="home-progress"
                type="indeterminate"
            />
        )}

        {!favoriteLoading && !favoriteTeam && (
            <div className="favorite-empty-state">
                <IonIcon icon={americanFootballOutline} />

                <strong>Aún no tienes un equipo favorito</strong>

                <span>
                    Selecciona un equipo desde tu perfil para seguir sus
                    resultados y próximos partidos.
                </span>
            </div>
        )}

        {!favoriteLoading && favoriteTeam && (
            <>
                <button
                    className="favorite-team-card"
                    type="button"
                    onClick={function () {
                        window.location.href = `/team/${favoriteTeam.id}`;
                    }}
                >
                    {favoriteTeam.logo ? (
                        <img
                            src={favoriteTeam.logo}
                            alt={`Logo de ${favoriteTeam.nombre}`}
                        />
                    ) : (
                        <span className="favorite-team-card__placeholder">
                            {favoriteTeam.nombre
                                ?.charAt(0)
                                .toUpperCase() || "E"}
                        </span>
                    )}

                    <span>
                        <small>TU EQUIPO FAVORITO</small>
                        <strong>{favoriteTeam.nombre}</strong>
                    </span>

                    <IonIcon icon={chevronForward} />
                </button>

                <div className="favorite-games-grid">
                    <article className="favorite-game-card">
                        <span className="favorite-game-card__eyebrow">
                            ÚLTIMO PARTIDO
                        </span>

                        {lastFavoriteGame ? (
                            <button
                                type="button"
                                onClick={function () {
                                    window.location.href =
                                        `/game/${lastFavoriteGame.id}`;
                                }}
                            >
                                <small>
                                    {formatCompactDate(
                                        lastFavoriteGame.fecha
                                    )}{" "}
                                    · {getFavoriteVenue(lastFavoriteGame)}
                                </small>

                                <strong>
                                    {favoriteTeam.nombre}{" "}
                                    <b>
                                        {getFavoriteTeamScore(
                                            lastFavoriteGame
                                        )}{" "}
                                        -{" "}
                                        {getOpponentScore(
                                            lastFavoriteGame
                                        )}
                                    </b>{" "}
                                    {getFavoriteOpponent(lastFavoriteGame)}
                                </strong>

                                <span>Ver resultado</span>
                            </button>
                        ) : (
                            <p>No hay resultados registrados.</p>
                        )}
                    </article>

                    <article className="favorite-game-card">
                        <span className="favorite-game-card__eyebrow">
                            PRÓXIMO PARTIDO
                        </span>

                        {nextFavoriteGame ? (
                            <button
                                type="button"
                                onClick={function () {
                                    window.location.href =
                                        `/game/${nextFavoriteGame.id}`;
                                }}
                            >
                                <small>
                                    {formatCompactDate(
                                        nextFavoriteGame.fecha
                                    )}{" "}
                                    · {getFavoriteVenue(nextFavoriteGame)}
                                </small>

                                <strong>
                                    {favoriteTeam.nombre}{" "}
                                    <b>vs.</b>{" "}
                                    {getFavoriteOpponent(nextFavoriteGame)}
                                </strong>

                                <span>Ver partido</span>
                            </button>
                        ) : (
                            <p>No hay próximos partidos programados.</p>
                        )}
                    </article>
                </div>
            </>
        )}
    </section>
)}

                <section className="home-section">
                    <div className="home-section__heading">
                        <div>
                            <span className="home-section__eyebrow">
                                AGENDA
                            </span>

                            <h2>Partidos de la semana</h2>
                        </div>

                        <IonIcon
                            className="home-section__icon"
                            icon={calendarOutline}
                        />
                    </div>

                    {loading && (
                        <IonProgressBar
                            className="home-progress"
                            type="indeterminate"
                        />
                    )}

                    {!loading && error && (
                        <div className="home-empty-state">
                            <IonIcon icon={trophyOutline} />

                            <strong>No pudimos cargar los partidos</strong>

                            <span>{error}</span>

                            <IonButton
                                size="small"
                                onClick={function () {
                                    loadHomeData(true);
                                }}
                            >
                                Reintentar
                            </IonButton>
                        </div>
                    )}

                    {!loading && !error && gameGroups.length === 0 && (
                        <div className="home-empty-state">
                            <IonIcon icon={calendarOutline} />

                            <strong>No hay partidos programados</strong>

                            <span>
                                Aún no hay encuentros disponibles para esta semana.
                            </span>

                            <IonButton
                                fill="outline"
                                size="small"
                                routerLink="/tournment"
                            >
                                Ver competencias
                            </IonButton>
                        </div>
                    )}

                    {!loading &&
                        !error &&
                        gameGroups.map(function (group) {
                            return (
                                <IonCard className="competition-games-card" key={group.id}>
                                    <IonCardContent>
                                        <button
                                            className="competition-games-card__header"
                                            type="button"
                                            onClick={function () {
                                                window.location.href = `/results/${group.competition?.id}`;
                                            }}
                                        >
                                            <span className="competition-games-card__identity">
                                                {group.competition?.logo ? (
                                                    <img
                                                        src={group.competition.logo}
                                                        alt=""
                                                        className="competition-games-card__logo"
                                                    />
                                                ) : (
                                                    <span className="competition-games-card__logo-placeholder">
                                                        <IonIcon icon={trophyOutline} />
                                                    </span>
                                                )}

                                                <span>
                                                    <strong>
                                                        {group.competition?.nombre ||
                                                            "Competencia"}
                                                    </strong>

                                                    <small>
                                                        {formatDate(group.date)}
                                                    </small>
                                                </span>
                                            </span>

                                            <IonIcon icon={chevronForward} />
                                        </button>

                                        <div className="competition-games-card__games">
                                            {group.games.map(function (game) {
                                                const localLogo = getTeamLogo(game, "local");
                                                const visitorLogo = getVisitorLogo(game);
                                                const status = getGameStatus(game);

                                                return (
                                                    <button
                                                        className="game-row"
                                                        key={game.id}
                                                        type="button"
                                                        onClick={function () {
                                                            window.location.href = `/game/${game.id}`;
                                                        }}
                                                    >
                                                        <span className="game-row__team game-row__team--local">
                                                            <span>
                                                                {getTeamName(game, "local")}
                                                            </span>

                                                            {localLogo ? (
                                                                <img
                                                                    src={localLogo}
                                                                    alt=""
                                                                />
                                                            ) : null}
                                                        </span>

                                                        <span className="game-row__score">
                                                            <strong>
                                                                {game.resLocal ?? "—"} -{" "}
                                                                {game.resVisita ?? "—"}
                                                            </strong>

                                                            <IonBadge
                                                                color={
                                                                    status === "Final"
                                                                        ? "medium"
                                                                        : "primary"
                                                                }
                                                            >
                                                                {status}
                                                            </IonBadge>
                                                        </span>

                                                        <span className="game-row__team game-row__team--visitor">
                                                            {visitorLogo ? (
                                                                <img
                                                                    src={visitorLogo}
                                                                    alt=""
                                                                />
                                                            ) : null}

                                                            <span>
                                                                {getTeamName(game, "visita")}
                                                            </span>
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </IonCardContent>
                                </IonCard>
                            );
                        })}
                </section>
            </IonContent>
        </IonPage>
    );
};

export default Home;
